<template>
<div>
    <Breadcrumbs main="Admin" title="Organisation View" />
    <div class="container-fluid">
        <div v-if="loading && !org.id" class="text-center my-5">
            <b-spinner variant="primary"></b-spinner>
        </div>
        <div v-else-if="error && !org.id" class="text-center my-5 text-danger">
            Could not load this organisation.
        </div>

        <div v-else>
            <!-- Header -->
            <div class="card">
                <div class="card-body">
                    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center" style="gap: 16px;">
                        <div class="d-flex align-items-center" style="gap: 14px;">
                            <img v-if="org.logo" :src="org.logo" style="height: 4.5rem; width: 4.5rem; border-radius: 1rem; object-fit: cover; box-shadow: 0 1px 4px rgba(0,0,0,0.08);" alt="logo" />
                            <div>
                                <h5 class="mb-1">{{ org.name }}</h5>
                                <div style="color: gray;">Created {{ formatDate(org.created_at) }}</div>
                            </div>
                        </div>
                        <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
                            <div style="cursor: pointer;" v-b-tooltip.hover title="Click to toggle" @click="toggleVerification">
                                <b-badge :variant="statusVariant(org.verification_status)" style="text-transform: capitalize; font-size: 14px;">● {{ org.verification_status }}</b-badge>
                            </div>
                            <b-button size="sm" variant="outline-primary" @click="$refs.editModal.open(org)">Edit</b-button>
                            <b-button size="sm" variant="outline-danger" v-b-modal.modal-org-delete>Delete</b-button>
                        </div>
                    </div>
                    <b-alert show variant="info" class="mt-3 mb-0">
                        Verifying an organisation doesn't verify its members. Review each member individually.
                    </b-alert>
                </div>
            </div>

            <div class="row">
                <div class="col-md-5">
                    <div class="card">
                        <div class="card-body">
                            <div class="mb-2">
                                <h7 style="color: gray;">Registration Number</h7>
                                <h6 style="margin-top: 2px;">{{ org.registration_number || 'N/A' }}</h6>
                            </div>
                            <div class="mb-2">
                                <h7 style="color: gray;">Country</h7>
                                <h6 style="margin-top: 2px;">{{ org.country || 'N/A' }}</h6>
                            </div>
                            <div class="mb-2">
                                <h7 style="color: gray;">Address</h7>
                                <h6 style="margin-top: 2px;">{{ org.address || 'N/A' }}</h6>
                            </div>
                            <div class="mb-2">
                                <h7 style="color: gray;">Website</h7>
                                <h6 style="margin-top: 2px;">
                                    <a v-if="org.website" :href="org.website" target="_blank" rel="noopener noreferrer">{{ org.website.slice(0, 50) }}</a>
                                    <span v-else>N/A</span>
                                </h6>
                            </div>
                            <div class="mb-2">
                                <h7 style="color: gray;">Contact</h7>
                                <div v-if="hasContact" style="margin-top: 2px;">
                                    <div v-if="org.contact.email">{{ org.contact.email }}</div>
                                    <div v-if="org.contact.phone">{{ org.contact.phone }}</div>
                                    <div v-if="org.contact.address">{{ org.contact.address }}</div>
                                </div>
                                <h6 v-else>N/A</h6>
                            </div>
                            <div>
                                <h7 style="color: gray;">Expertise Areas</h7>
                                <div v-if="org.expertise_areas && org.expertise_areas.length" class="d-flex flex-wrap" style="gap: 4px; margin-top: 4px;">
                                    <b-badge v-for="area in org.expertise_areas" :key="area" variant="info" style="text-transform: capitalize;">{{ area }}</b-badge>
                                </div>
                                <h6 v-else>N/A</h6>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-7">
                    <div class="card">
                        <div class="card-body">
                            <h7 style="color: gray;">About</h7>
                            <div v-if="org.bio" style="margin-top: 4px; white-space: pre-line;">{{ org.bio }}</div>
                            <h6 v-else>N/A</h6>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-body">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <h6 class="mb-0">Members</h6>
                                <router-link :to="{ path: '/admin/skilled-professionals', query: { organisation: org.id, status: '' } }">
                                    <b-button size="sm" variant="outline-primary">Open in review queue</b-button>
                                </router-link>
                            </div>
                            <div v-if="members.length" class="d-flex flex-wrap" style="gap: 16px;">
                                <div v-for="member in members" :key="member.id" class="p-2" style="border: 1px solid lightgray; border-radius: 8px; min-width: 240px;">
                                    <div><strong>{{ [member.user?.first_name, member.user?.last_name].filter(Boolean).join(' ') || 'N/A' }}</strong></div>
                                    <div v-if="member.user?.email">{{ member.user.email }}</div>
                                    <div class="mt-1">
                                        Role: <b-badge :variant="member.role === 'admin' ? 'info' : 'primary'" style="text-transform: capitalize;">{{ member.role }}</b-badge>
                                    </div>
                                    <div class="mt-1">
                                        <template v-if="member.professional_status">
                                            Profile: <b-badge :variant="statusVariant(member.professional_status)" style="text-transform: capitalize;">{{ member.professional_status }}</b-badge>
                                        </template>
                                        <b-badge v-else variant="light" v-b-tooltip.hover title="No individual profile; only manages the organisation and won't appear in the review queue">Manages organisation only</b-badge>
                                    </div>
                                    <div v-if="member.assigned_date" style="color: gray; font-size: 12px;">Joined {{ formatDate(member.assigned_date) }}</div>
                                    <router-link v-if="member.professional_status && profileIdFor(member)" :to="`/admin/skilled-professional-view/${profileIdFor(member)}`">
                                        <b-button size="sm" class="mt-2" :variant="member.professional_status === 'submitted' ? 'primary' : 'outline-primary'">
                                            {{ member.professional_status === 'submitted' ? 'Review' : 'View profile' }}
                                        </b-button>
                                    </router-link>
                                </div>
                            </div>
                            <h6 v-else>N/A</h6>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <sp-organisation-edit-modal ref="editModal" @saved="refresh" />

    <b-modal id="modal-org-delete" centered hide-header @ok="handleDeleteOk">
        <div class="mt-4 mb-4" style="font-size: large;">Are you sure you want to delete this organisation?</div>
    </b-modal>
</div>
</template>

<script>
import { mapState } from 'vuex';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import { statusVariant, formatDate } from './skilled-professional-helpers';
import SpOrganisationEditModal from './sp-organisation-edit-modal.vue';

export default {
    components: { SpOrganisationEditModal },

    computed: {
        ...mapState({
            detail: state => state.skilledProfessional.organisationDetail,
            professionals: state => state.skilledProfessional.organisationMembers,
            loading: state => state.skilledProfessional.organisationLoading,
            error: state => state.skilledProfessional.organisationError
        }),

        org() {
            return this.detail && String(this.detail.id) === String(this.$route.params.id) ? this.detail : {};
        },

        members() {
            return this.org.members || [];
        },

        hasContact() {
            const c = this.org.contact || {};
            return !!(c.email || c.phone || c.address);
        }
    },

    watch: {
        '$route.params.id': {
            handler() {
                this.refresh();
            },
            immediate: true
        }
    },

    methods: {
        refresh() {
            const id = this.$route.params.id;
            if (!id) return;
            this.$store.dispatch('skilledProfessional/fetchOrganisationDetail', id);
            this.$store.dispatch('skilledProfessional/fetchOrganisationMembers', id);
        },

        // Members carry the membership id; match on user id to find their professional profile id
        profileIdFor(member) {
            const userId = member.user?.id;
            const profile = (this.professionals || []).find(p => p.user?.id === userId);
            return profile ? profile.id : null;
        },

        toggleVerification() {
            const next = this.org.verification_status === 'verified' ? 'pending' : 'verified';
            Swal.fire({
                title: `Mark as ${next}?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes'
            }).then(result => {
                if (!(result.isConfirmed || result.value)) return;
                this.$store.dispatch('skilledProfessional/toggleOrganisationVerification', this.org.id).then(response => {
                    Swal.fire({
                        title: response.code == 200 ? 'Success!' : 'Error!',
                        text: response.response,
                        icon: response.code == 200 ? 'success' : 'error'
                    });
                    this.refresh();
                });
            });
        },

        handleDeleteOk() {
            this.$store.dispatch('skilledProfessional/deleteOrganisation', this.org.id).then(response => {
                Swal.fire({
                    title: response.code == 200 ? 'Success!' : 'Error!',
                    text: response.response,
                    icon: response.code == 200 ? 'success' : 'error'
                });
                if (response.code == 200) {
                    this.$router.push('/admin/sp-organisations');
                }
            });
        },

        statusVariant,
        formatDate
    }
};
</script>
